import { RootState } from "integrations/redux/rootReducer"
import { Icon } from "@nova/protobuf"
import { memoize } from "nodash"
import { createSelector } from "@reduxjs/toolkit"
import { IconShape } from "@nova/constants"
import { iconShapeOptionFromUrl } from "features/map/inspector-panel/icons/module"
import { selectIconTemplate } from "plugins/Toolbox/tool-registry"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { getAncestors } from "panels/content/side-panel/utils"
import { compareFractionalIndices } from "@nova/serializer/src/client/util/fractional_indexing"
import { parseIconTargetPayload } from "plugins/IconTargetPayload"

const root = (state: RootState) => state.project.data.icons

export const selectAllIcons = createSelector([root], (state): Icon[] =>
    state.map((icon) => {
        return {
            ...icon,
            images: [...icon.images].sort((a, b) => compareFractionalIndices(a.index, b.index)),
        }
    })
)
export const selectNonArchivedIcons = createSelector([selectAllIcons], (icons) =>
    icons.filter((icon) => !icon.archivedTimestamp)
)

export const selectIconMap = createSelector([selectAllIcons], (icons) =>
    icons.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, Icon>
    )
)

export const selectIconsByTargetId = memoize((targetId: string) =>
    createSelector([selectAllIcons], (icons) =>
        icons.filter((icon) => {
            const parsed = parseIconTargetPayload<"cf-frame">(icon.targetPayload)
            return parsed?.target === "cf-frame" && parsed.targetId === targetId
        })
    )
)

export const selectIconById = memoize((id: string) =>
    createSelector([selectIconMap], (map) => map[id])
)

export const selectNextIconNumber = createSelector([selectAllIcons], (icons) => icons.length + 1)

export const selectVisibleIcons = createSelector(
    [selectNonArchivedIcons, selectAllEntitiesMap],
    (icons, entityMap) =>
        icons.filter(
            (icon) =>
                icon.visible &&
                !getAncestors(icon.id, entityMap).find((ancestor) => !ancestor.visible)
        )
)

export const selectIconColorById = memoize((id: string) =>
    createSelector([selectIconById(id)], (icon) => icon?.color)
)

const selectIconUrlById = memoize((id: string) =>
    createSelector([selectIconById(id)], (icon) => icon?.url)
)

export const selectIconShapeById = memoize((id: string) =>
    createSelector([selectIconUrlById(id)], (url) =>
        url?.startsWith("shape") ? (url as IconShape) : undefined
    )
)

export const selectIconPackById = memoize((id: string) =>
    createSelector([selectIconById(id), selectIconTemplate], (icon, template) =>
        iconShapeOptionFromUrl(icon?.url || template.url)
    )
)

export const selectIconOutlineById = memoize((id: string) =>
    createSelector([selectIconById(id)], (icon) => icon?.outline)
)

export const selectIconWidthById = memoize((id: string) =>
    createSelector([selectIconById(id)], (icon) => icon?.width)
)
